import { useCallback, useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { registerBusiness } from '../../api/onboardingApi';
import { authApi } from '../../api/authApi';
import { useRefreshOnVisibility } from '../../hooks/useRefreshOnVisibility';
import { storageSet } from '../../utils/storage';
import { logger } from '../../utils/logger';
import Step1BusinessName from './Step1BusinessName';
import Step2BotToken from './Step2BotToken';
import Step3Languages from './Step3Languages';
import StepSuccess from './StepSuccess';

function isTokenExpired(token: string): boolean {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

type Step = 1 | 2 | 3 | 'success';

interface FormState {
  businessName: string;
  botToken: string;
  defaultLanguage: string;
  supportedLanguages: string[];
}

export default function OwnerOnboardingPage() {
  const { t } = useTranslation('owner');
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>({
    businessName: '',
    botToken: '',
    defaultLanguage: 'uz',
    supportedLanguages: ['uz'],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [botTokenError, setBotTokenError] = useState<string | undefined>();
  const [result, setResult] = useState<{ businessName: string; botUsername: string } | null>(null);

  useEffect(() => {
    WebApp.ready();
    WebApp.expand();
  }, []);

  // Re-expand the Telegram viewport when the WebApp is re-activated
  // (e.g. user returned after Android hardware back). Without this the
  // viewport can stay collapsed at 0 height and the page renders blank.
  const handleReopen = useCallback(() => {
    WebApp.expand();
  }, []);
  useRefreshOnVisibility(handleReopen);

  const handleSubmit = async () => {
    const user = WebApp.initDataUnsafe?.user;

    if (!user?.id) {
      message.error(t('onboarding.errors.telegramUserMissing'));
      return;
    }

    setIsLoading(true);
    try {
      try {
        logger.info('Refreshing JWT via telegram-login before onboarding register');
        const authResponse = await authApi.telegramLogin();
        await storageSet('accessToken', authResponse.accessToken);
        await storageSet('refreshToken', authResponse.refreshToken);
        logger.info('telegram-login succeeded, proceeding with register');
      } catch (authErr: unknown) {
        const e = authErr as { response?: { status?: number; data?: { code?: string } } };
        const status = e?.response?.status;
        const code = e?.response?.data?.code;

        const existingToken = localStorage.getItem('accessToken');
        if (!existingToken || isTokenExpired(existingToken)) {
          if (status === 422 && code === 'OTP_REQUIRED') {
            message.error(t('onboarding.errors.otpRequired'));
          } else {
            message.error(t('onboarding.errors.authFailed'));
          }
          logger.error('telegram-login failed and no valid token available', { status, code });
          setIsLoading(false);
          return;
        }
        logger.warn('telegram-login failed, falling back to existing token', { status });
      }

      const response = await registerBusiness({
        botToken: form.botToken,
        businessName: form.businessName,
        defaultLanguage: form.defaultLanguage,
        supportedLanguages: form.supportedLanguages,
        telegramId: user.id,
        username: user.username ?? null,
        firstName: user.first_name ?? null,
        lastName: user.last_name ?? null,
        languageCode: user.language_code ?? null,
      });

      storageSet('accessToken', response.tokens.accessToken);
      storageSet('refreshToken', response.tokens.refreshToken);

      setResult({
        businessName: response.tenant.businessName,
        botUsername: response.tenant.botUsername,
      });

      logger.info('Onboarding completed', {
        tenantId: response.tenant.id,
        newTenant: response.newTenant,
      });

      setStep('success');
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string } } };
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message;

      if (status === 400 || status === 404) {
        setBotTokenError(t('onboarding.step2.serverErrors.invalid'));
        setStep(2);
      } else if (status === 409) {
        setBotTokenError(t('onboarding.step2.serverErrors.conflict'));
        setStep(2);
      } else {
        message.error(serverMessage ?? t('onboarding.errors.generic'));
        logger.error('Onboarding failed', { status, serverMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateForm = (partial: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...partial }));
  };

  if (step === 'success' && result) {
    return <StepSuccess businessName={result.businessName} botUsername={result.botUsername} />;
  }

  if (step === 1) {
    return (
      <Step1BusinessName
        value={form.businessName}
        onChange={(v) => updateForm({ businessName: v })}
        onNext={() => setStep(2)}
      />
    );
  }

  if (step === 2) {
    return (
      <Step2BotToken
        value={form.botToken}
        onChange={(v) => {
          updateForm({ botToken: v });
          setBotTokenError(undefined);
        }}
        onNext={() => setStep(3)}
        onBack={() => setStep(1)}
        serverError={botTokenError}
      />
    );
  }

  return (
    <Step3Languages
      supportedLanguages={form.supportedLanguages}
      defaultLanguage={form.defaultLanguage}
      onChange={(supported, defaultLang) =>
        updateForm({ supportedLanguages: supported, defaultLanguage: defaultLang })
      }
      onNext={handleSubmit}
      onBack={() => setStep(2)}
      isLoading={isLoading}
    />
  );
}
